import dayjs from 'dayjs'

import { getForgeContext } from '@/utils/forge-helpers/forge-context'
import { forgeView } from '@/utils/forge-helpers/general'
import { getPluginOption } from '@/utils/plugin-options'
import { IS_DEVELOPMENT } from '@root/app-modes'

export const PLUGIN_TYPES = {
  cloud: 'cloud'
}

/* Return current jira instance url without slash at the end.
 * On Jira server it includes also path to plugin, not just host! */
export const getAtlassianBaseUrl = () => {
  if (IS_DEVELOPMENT) {
    return location.origin
  }

  let baseUrl = getPluginOption('atlassianBaseUrl')
  if (!baseUrl) {
    throw new Error(
      "Atlassian's baseUrl doesn't set. You have to set data-attr in template-options.js"
    )
  } else {
    if (baseUrl[baseUrl.length - 1] === '/') {
      baseUrl = baseUrl.slice(0, baseUrl.length - 1)
    }

    return baseUrl
  }
}

/* Return current jira instance url without slash at the end. */
export const getBaseUrl = () => {
  let baseUrl = getPluginOption('baseUrl')
  if (!baseUrl) {
    throw new Error("Atlassian's baseUrl doesn't set. You have to set it in template-options.js")
  }

  if (baseUrl[baseUrl.length - 1] === '/') {
    baseUrl = baseUrl.slice(0, baseUrl.length - 1)
  }

  return baseUrl
}

export const getUserAvatarUrl = user => user.avatarUrl

export const getAtlassianUserUrl = userId => {
  const base = getAtlassianBaseUrl()

  return `${base}/people/${userId}`
}

export const getAtlassianIssueUrl = issueId => {
  const base = getBaseUrl()

  return `${base}/browse/${issueId}`
}

export const getAtlassianIssueUrlForWebApp = issueId => {
  const base = getAtlassianBaseUrl()

  return `${base}/browse/${issueId}`
}

export const getAtlassianIssueUrlForCrossPlatformApp = issueId => {
  const base = getAtlassianBaseUrl()

  return `${base}/browse/${issueId}`
}

export const transformLocaleToDayjsFormat = locale => locale.replaceAll('_', '-').toLowerCase()

const getAvailableLocale = ({ locale, availableLocales, fallbackLocale }) => {
  // locales from JIRA
  // Do not use composable function here, because Jira will skip it

  if (availableLocales.includes(locale)) {
    return locale
  }

  // our locales
  // ['de_DE', 'en', 'fr', 'it', 'pt_BR', 'uk', 'zh_CN']
  // locale model in forge can be founded in view.getContenxt().locale

  const LOCALES_MAP = {
    en_us: 'en',
    en_gb: 'en',
    zh_cn: 'zh_CN',
    zh_tw: 'zh_TW',
    fr_fr: 'fr',
    de_de: 'de_DE',
    it_it: 'it',
    pt_br: 'pt_BR',
    pt_pt: 'pt_BR',
    ru_ru: 'uk',
    uk_ua: 'uk',
    ua_ua: 'uk'
  }

  const JIRA_LOCALES_MAP = {
    ...LOCALES_MAP,
    ...Object.fromEntries(
      Object.entries(LOCALES_MAP).map(([key, value]) => [key.replaceAll('_', '-'), value])
    )
  }

  return JIRA_LOCALES_MAP[locale.toLowerCase()] || fallbackLocale.value
}

export const setLocaleFromJira = async ({ locale, fallbackLocale, availableLocales }) => {
  if (!forgeView) return

  const context = await getForgeContext({ fresh: true })

  const jiraLocale = context.locale || null

  if (!jiraLocale) return

  const lang = getAvailableLocale({ locale: jiraLocale, fallbackLocale, availableLocales })
  locale.value = lang
  dayjs.locale(transformLocaleToDayjsFormat(lang))
}
